import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Activity,
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Clock3,
  GitBranch,
  Hash,
  Loader2,
  Network,
  PlayCircle,
  Router,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/lib/api-client'
import type { Job, Validation } from '@/lib/api-client'
import { DetailHeader, DetailMetric, DetailPage, DetailSection, DetailTab, DetailTabBar } from '@/components/detail-page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

const isValidationInFlight = (validation: Validation) =>
  validation.status === 'running' || validation.status === 'pending' || validation.status === 'queued'

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return 'N/A'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return 'N/A'
  }

  return date.toLocaleString()
}

const formatShortId = (value?: string | null) => {
  if (!value) {
    return 'Unknown'
  }

  return value.slice(-8)
}

const getValidationTypeLabel = (type: string) => {
  switch (type) {
    case 'analysis':
      return 'Analysis'
    case 'simulation':
      return 'Simulation'
    default:
      return type.charAt(0).toUpperCase() + type.slice(1)
  }
}

const getValidationOutcome = (validation: Validation) => validation.result || validation.status

const getJobStatus = (validations: Validation[]) => {
  if (!validations.length) {
    return 'pending'
  }
  if (validations.some((validation) => validation.status === 'running')) {
    return 'running'
  }
  if (validations.some((validation) => validation.status === 'pending' || validation.status === 'queued')) {
    return 'pending'
  }
  if (validations.some((validation) => validation.result === 'failure' || validation.status === 'failed' || validation.status === 'errored')) {
    return 'failure'
  }
  return 'success'
}

const getJobHeading = (status: string) => {
  switch (status) {
    case 'running':
      return 'Job active'
    case 'pending':
      return 'Job queued'
    case 'failure':
      return 'Job needs review'
    case 'success':
      return 'Job completed'
    default:
      return 'Job detail'
  }
}

const getJobSummary = (status: string, activeValidation: Validation | null, hasValidations: boolean) => {
  if (activeValidation) {
    return `${getValidationTypeLabel(activeValidation.type)} validation is running.`
  }
  if (status === 'success') {
    return 'Validation records are available for this job.'
  }
  if (hasValidations) {
    return 'Review the validations created by this job.'
  }
  return 'Validations have not been created yet.'
}

const getJobSurfaceDescription = (job: Job, status: string, validation: Validation | null, hasValidations: boolean) => {
  const triggerSource = job.source || job.triggerType || 'API'
  const activeValidation = validation && isValidationInFlight(validation) ? validation : null
  return `Triggered by ${triggerSource} on ${formatDateTime(job.createdAt)}. ${getJobSummary(status, activeValidation, hasValidations)}`
}

const getStatusBadgeTone = (status: string) => {
  switch (status) {
    case 'success':
      return 'border-success/20 bg-success/10 text-success'
    case 'failure':
    case 'failed':
    case 'errored':
      return 'border-destructive/20 bg-destructive/10 text-destructive'
    case 'running':
      return 'border-info/20 bg-info/10 text-info'
    case 'pending':
    case 'queued':
      return 'border-warning/20 bg-warning/10 text-warning'
    default:
      return 'border-border/60 bg-background text-foreground'
  }
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'success':
      return <CheckCircle2 className="h-5 w-5 text-success" />
    case 'failure':
    case 'failed':
    case 'errored':
      return <AlertCircle className="h-5 w-5 text-destructive" />
    case 'running':
      return <Loader2 className="h-5 w-5 animate-spin text-info" />
    case 'pending':
    case 'queued':
      return <Clock3 className="h-5 w-5 text-warning" />
    default:
      return <Activity className="h-5 w-5 text-muted-foreground" />
  }
}

export default function JobPage() {
  const params = useParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [job, setJob] = useState<Job | null>(null)
  const [validations, setValidations] = useState<Validation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
    } else if (params.id) {
      void fetchJob()
      void fetchValidations()
    }
  }, [isAuthenticated, params.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const shouldPoll = validations.length === 0 || validations.some(isValidationInFlight)
    if (!shouldPoll) {
      return
    }

    const interval = window.setInterval(() => {
      void fetchJob()
      void fetchValidations()
    }, 3000)

    return () => window.clearInterval(interval)
  }, [validations]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchJob = async () => {
    try {
      setError(null)
      const response = await apiClient.getJob(params.id!)
      if (response.success && response.data) {
        setJob((response.data as { job: Job }).job)
      } else {
        setError(response.error || 'Failed to load job')
      }
    } catch {
      setError('Failed to load job')
    } finally {
      setLoading(false)
    }
  }

  const fetchValidations = async () => {
    try {
      const response = await apiClient.getJobValidations(params.id!)
      if (response.success && response.data) {
        setValidations(response.data.validations || [])
      }
    } catch {
      // Keep the job shell usable if secondary fetches fail.
    }
  }

  const orderedValidations = useMemo(
    () => [...validations].sort((left, right) => {
      const leftTime = new Date(left.startedAt || left.createdAt || 0).getTime()
      const rightTime = new Date(right.startedAt || right.createdAt || 0).getTime()
      return rightTime - leftTime
    }),
    [validations],
  )

  if (loading) {
    return (
      <DetailPage>
        <Card className="rounded-xl border-border/30 shadow-none">
          <CardContent className="flex min-h-[18rem] flex-col items-center justify-center text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="mt-3 text-sm text-muted-foreground">Loading job...</p>
          </CardContent>
        </Card>
      </DetailPage>
    )
  }

  if (error) {
    return (
      <DetailPage>
        <Card className="rounded-xl border-destructive/40 shadow-none">
          <CardContent className="flex items-center gap-3 pt-6">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <span className="text-destructive">{error}</span>
          </CardContent>
        </Card>
      </DetailPage>
    )
  }

  if (!job) {
    return null
  }

  const jobStatus = getJobStatus(orderedValidations)
  const activeValidation = orderedValidations.find(isValidationInFlight) || null
  const latestValidation = activeValidation || orderedValidations[0] || null
  const selectedValidation = latestValidation
  const hasValidations = orderedValidations.length > 0

  return (
    <DetailPage>
      <DetailHeader
        breadcrumbs={(
          <>
            {job.networkId ? (
              <>
                <button type="button" onClick={() => navigate(`/networks/${job.networkId}`)} className="transition-colors hover:text-foreground">
                  {job.networkName || 'Network'}
                </button>
                <ChevronRight className="h-4 w-4" />
              </>
            ) : (
              <>
                <button type="button" onClick={() => navigate('/overview')} className="transition-colors hover:text-foreground">
                  Overview
                </button>
                <ChevronRight className="h-4 w-4" />
              </>
            )}
            <span className="font-semibold text-foreground">Job #{formatShortId(job.id)}</span>
          </>
        )}
        icon={getStatusIcon(jobStatus)}
        title={getJobHeading(jobStatus)}
        description={getJobSurfaceDescription(job, jobStatus, selectedValidation, hasValidations)}
        metrics={(
          <>
            <DetailMetric label="Status" value={jobStatus.toUpperCase()} />
            <DetailMetric label="Validations" value={orderedValidations.length} detail={job.networkName || 'No linked network'} />
            <DetailMetric label="Created" value={formatDateTime(job.createdAt)} />
          </>
        )}
        actions={(
          <>
            {latestValidation ? (
              <Button onClick={() => navigate(`/validations/${latestValidation.id}`)}>
                Open {activeValidation ? 'Active' : 'Latest'} Validation
              </Button>
            ) : null}
          </>
        )}
        tabs={(
          <DetailTabBar>
            <DetailTab icon={<Router className="h-4 w-4" />} label="Checks" meta={orderedValidations.length} active />
            <DetailTab icon={<Hash className="h-4 w-4" />} label="Launch Context" />
          </DetailTabBar>
        )}
      />

      <section className="overflow-hidden rounded-xl border border-border/30 bg-background">
        <div className="flex min-h-[34rem] flex-col lg:flex-row">
          <aside className="w-full border-b border-border/20 bg-muted lg:w-80 lg:border-b-0 lg:border-r lg:border-r-border/20">
            <div className="flex items-center justify-between border-b border-border/20 px-4 py-3">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Job outputs</span>
              <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-bold', getStatusBadgeTone(jobStatus))}>
                {jobStatus.toUpperCase()}
              </span>
            </div>
            {!hasValidations ? (
              <div className="px-4 py-8 text-center">
                <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-primary" />
                <p className="text-sm font-semibold text-foreground">Waiting for validations to be created</p>
                <p className="mt-2 text-sm text-muted-foreground">Processing job and creating validations...</p>
              </div>
            ) : (
              <div className="divide-y divide-border/20">
                {orderedValidations.map((validation) => {
                  const outcome = getValidationOutcome(validation).toUpperCase()
                  const selected = selectedValidation?.id === validation.id

                  return (
                    <button
                      key={validation.id}
                      type="button"
                      className={cn(
                        'flex w-full items-start gap-3 border-l-[3px] border-l-transparent px-4 py-4 text-left transition-colors hover:bg-background/70',
                        selected && 'border-l-primary bg-background',
                      )}
                      onClick={() => navigate(`/validations/${validation.id}`)}
                    >
                      <span className="mt-0.5 shrink-0">{getStatusIcon(getValidationOutcome(validation))}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-foreground">{getValidationTypeLabel(validation.type)}</span>
                        <span className="mt-1 block text-[11px] leading-5 text-muted-foreground">
                          {isValidationInFlight(validation) ? 'Still executing' : 'Ready for validation triage'}
                        </span>
                        <span className="mt-1 block text-[11px] text-muted-foreground">{formatDateTime(validation.startedAt || validation.createdAt)}</span>
                      </span>
                      <span className="shrink-0 text-[10px] font-semibold text-muted-foreground">{outcome}</span>
                    </button>
                  )
                })}
              </div>
            )}
          </aside>

          <div className="flex-1 space-y-6 bg-background p-5 sm:p-6">
            <DetailSection
              title="Orchestration status"
              description="Current job status."
              action={latestValidation ? (
                <Button size="sm" onClick={() => navigate(`/validations/${latestValidation.id}`)}>
                  Open Active Validation
                </Button>
              ) : undefined}
              contentClassName="space-y-4"
            >
              <div className={cn('rounded-lg border px-4 py-4', getStatusBadgeTone(jobStatus))}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Job state</p>
                    <p className="mt-2 text-xl font-semibold text-foreground">{jobStatus.toUpperCase()}</p>
                    <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
                      {getJobSummary(jobStatus, activeValidation, hasValidations)}
                    </p>
                  </div>
                  <span className={cn('rounded border px-2 py-1 text-xs font-semibold', getStatusBadgeTone(jobStatus))}>
                    {jobStatus.toUpperCase()}
                  </span>
                </div>
              </div>

              {selectedValidation ? (
                <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_15rem]">
                  <div className="rounded-lg border border-border/30 bg-background px-4 py-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-foreground">Current validation</p>
                      <Badge variant="outline">{getValidationOutcome(selectedValidation).toUpperCase()}</Badge>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {isValidationInFlight(selectedValidation)
                        ? 'This validation is still running.'
                        : 'This is the newest validation for the job.'}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/validations/${selectedValidation.id}`)}>
                        View validation
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="rounded-lg border border-border/30 bg-muted px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Current validation</p>
                    <p className="mt-2 text-sm font-medium text-foreground">{getValidationOutcome(selectedValidation).toUpperCase()}</p>
                    <p className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Started</p>
                    <p className="mt-2 text-sm text-foreground">{formatDateTime(selectedValidation.startedAt || selectedValidation.createdAt)}</p>
                  </div>
                </div>
              ) : null}
            </DetailSection>

            <DetailSection
              title="Launch context"
              description="Job identifiers and trigger details."
              contentClassName="grid gap-3 md:grid-cols-2 xl:grid-cols-3"
            >
              {[
                { label: 'Job ID', value: job.id, icon: Hash },
                { label: 'Branch', value: job.branch || 'N/A', icon: GitBranch },
                { label: 'Trigger', value: job.triggerType || 'N/A', icon: PlayCircle },
                { label: 'Network', value: job.networkName || 'Unknown', icon: Network },
                { label: 'Source', value: job.source || 'API', icon: Activity },
                { label: 'Created', value: formatDateTime(job.createdAt), icon: Clock3 },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="rounded-lg border border-border/30 bg-background px-4 py-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Icon className="h-4 w-4 text-primary" />
                    {label}
                  </div>
                  <p className="mt-2 break-all text-sm text-muted-foreground">{value}</p>
                </div>
              ))}
              <div className="rounded-lg border border-border/30 bg-background px-4 py-4">
                <p className="text-sm font-semibold text-foreground">Commit SHA</p>
                <p className="mt-2 break-all font-mono text-xs text-muted-foreground">{job.commitSHA || 'N/A'}</p>
              </div>
            </DetailSection>

            {hasValidations ? (
              <DetailSection
                title="Validations"
                description="Validations created by this job."
                contentClassName="space-y-3"
              >
                {orderedValidations.map((validation) => (
                  <div key={validation.id} className="flex flex-col gap-3 rounded-lg border border-border/30 bg-background px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">{getValidationTypeLabel(validation.type)} validation</p>
                      <p className="mt-1 text-sm text-muted-foreground">{formatDateTime(validation.startedAt || validation.createdAt)}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Validation ID</p>
                        <p className="font-mono text-xs text-foreground">{validation.id}</p>
                      </div>
                      <Badge variant="outline" className={getStatusBadgeTone(getValidationOutcome(validation))}>
                        {getValidationOutcome(validation).toUpperCase()}
                      </Badge>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/validations/${validation.id}`)}>
                        Open validation
                      </Button>
                    </div>
                  </div>
                ))}
              </DetailSection>
            ) : null}
          </div>
        </div>
      </section>
    </DetailPage>
  )
}
